// ── TTS Model ──────────────────────────────────────────────────────────
export type TtsModel =
  | "chatterbox"
  | "qwen3-tts"
  | "fish-s2-pro"
  | "moss-tts-local"
  | "moss-tts-delay";

/**
 * How MOSS-TTS is prompted with the reference voice. "reference" (default) uses
 * the plain reference slot. "transcript" also feeds the saved clip transcript as
 * an in-context turn; OpenMOSS documents that as improving speaker similarity,
 * but it measured worse on both identity and pacing, so it is opt-in.
 */
export type MossCloneMode = "transcript" | "reference";

// ── Multi-take ─────────────────────────────────────────────────────────
export type TakeStatus = "queued" | "processing" | "complete" | "failed";

export interface Take {
  takeIndex: number;
  status: TakeStatus;
  outputKey?: string;
  // Presigned GET URL for outputKey, populated by getJob/listJobs on the
  // server. Each take has its own audioUrl, share button, and download link.
  audioUrl?: string;
  duration?: number;
  genTimeSeconds?: number;
  inferenceSeconds?: number;
  // 0–100. Streamed by the worker ~1/sec while the take's LM step runs.
  progress?: number;
  error?: string;
  completedAt?: string;
  // Base64-encoded uint8 array of 480 normalized peak amplitudes (0..255). When
  // present, sample-card waveforms paint without downloading the audio file.
  peaks?: string;
}

// ── Job ────────────────────────────────────────────────────────────────
export interface Job {
  jobId: string;
  userId: string;
  type: "tts" | "voice-convert";
  status: "queued" | "processing" | "complete" | "failed" | "cancelled";
  progress: number;

  text?: string;
  referenceName: string;
  referenceKey: string;
  model?: TtsModel;
  referenceTranscript?: string;
  exaggeration?: number;
  cfgWeight?: number;
  temperature?: number;
  repetitionPenalty?: number;
  topP?: number;
  minP?: number;
  // moss-tts: audio_top_k, and how the reference is prompted.
  topK?: number;
  cloneMode?: MossCloneMode;
  trimStart?: number;
  trimEnd?: number;
  // fish-s2-pro: ISO 639-1 code or "auto"
  language?: string;

  sourceKey?: string;
  diffusionSteps?: number;
  similarityCfgRate?: number;
  intelligibilityCfgRate?: number;
  lengthAdjust?: number;

  // Display overrides (per-sample customization)
  displayName?: string;
  displayImageUrl?: string;

  // Legacy single-take output. New jobs leave outputKey/audioUrl/duration on
  // the parent undefined and use the takes[] array instead. The API
  // normalizeTakes() shim ensures legacy jobs still surface as a 1-element
  // takes[] on the wire.
  outputKey?: string;
  audioUrl?: string;
  sourceAudioUrl?: string;
  referenceAudioUrl?: string;
  duration?: number;
  genTimeSeconds?: number;

  // Multi-take output. Always populated for new jobs (the API shim fills it
  // for legacy jobs too, so the UI can iterate uniformly).
  takeCount?: number;
  takes?: Take[];

  // Cost tracking
  gpuType?: string;
  gpuCostPerSec?: number;
  inferenceSeconds?: number;
  totalJobCost?: number;

  // Error info
  error?: string;
  logs?: string;

  userAgent?: string;
  ipAddress?: string;
  deviceType?: string;

  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  ttl: number;

  // UI-only flags (not from API)
  _exiting?: boolean;
  _prevStatus?: string;
}

// ── User Profile ───────────────────────────────────────────────────────
export interface UserProfile {
  userId: string;
  email: string;
  role: "user" | "super_admin";
  status: "active" | "suspended";
  createdAt: string;
  lastLoginAt?: string;
  jobCount: number;
  totalGenSeconds: number;
}

// ── Voice Defaults ─────────────────────────────────────────────────────
export interface VoiceDefaults {
  exaggeration?: number;
  cfgWeight?: number;
  temperature?: number;
  repetitionPenalty?: number;
  topP?: number;
  minP?: number;
}

// ── Voice Preview status ───────────────────────────────────────────────
export type VoicePreviewStatus = "queued" | "processing" | "complete" | "failed";

// ── Voice Reference ────────────────────────────────────────────────────
export interface VideoRefImage {
  /** S3 key under reference-images/{userId}/. */
  key: string;
  /** What it is, in the user's words — "the man", "the kitchen". */
  label?: string;
}

export interface Reference {
  name: string;
  // Original upload. Never mutated by save-default (the "Change default" UI
  // fetches this so the user can re-trim).
  s3Key: string;
  duration: number;
  uploadedAt: string;
  previewUrl?: string;
  provided?: boolean;
  ownerUserId?: string;
  defaults?: VoiceDefaults;
  // Trim boundaries relative to the ORIGINAL s3Key audio.
  defaultTrimStart?: number;
  defaultTrimEnd?: number;
  // Pre-extracted clip of (defaultTrimStart, defaultTrimEnd). When present, TTS
  // uses this key and Modal skips its on-the-fly ffmpeg trim. Use effectiveReferenceKey().
  defaultClipS3Key?: string;
  defaultClipDuration?: number;
  // Presigned GET URL for defaultClipS3Key (parallel to previewUrl, which always
  // points at the original s3Key for the "Change default" re-trim flow).
  defaultClipPreviewUrl?: string;
  // Presigned GET for the cropped 1:1 derivative (display image).
  imageUrl?: string;
  // Presigned GET for the original full-size image, used by the editor for re-cropping.
  imageOriginalUrl?: string;
  // Crop region (0-1 relative) on the original; lets the editor restore the
  // last-saved frame so the user can fine-tune without re-uploading.
  imageCrop?: { x: number; y: number; w: number; h: number };
  sourceUrl?: string;
  transcript?: string;

  // ── Video reference images ────────────────────────────────────────────
  // Reference images (and their labels) saved against this voice, preloaded
  // into the video panel whenever a job using this voice generates a video.
  // Distinct from imageUrl above, which is the single square library-card
  // hero: these are the MiniMax-H3 r2v references, there can be several, and
  // each carries the label that gives it its own <Subject N> in the prompt.
  videoRefImages?: VideoRefImage[];
  // Presigned GETs for videoRefImages, added by listReferences so the video
  // panel can paint the tiles. Not stored.
  videoRefImageUrls?: string[];

  // Voice preview — TTS render of a fixed standard script in this voice
  // (Fish S2-Pro). Lets users compare voices apples-to-apples without
  // committing to a real generate job. Auto-generated on reference create
  // and refreshable via admin backfill.
  voicePreviewS3Key?: string;
  voicePreviewUrl?: string;          // presigned GET, hydrated server-side
  voicePreviewStatus?: VoicePreviewStatus;
  voicePreviewGeneratedAt?: string;
  voicePreviewQueuedAt?: string;
  voicePreviewScriptVersion?: string;
  voicePreviewError?: string;

  // Base64-encoded uint8 array of 480 normalized peak amplitudes describing
  // voicePreviewUrl. Absent on legacy rows — the UI lazily backfills via
  // POST /audio/peaks on first decode.
  voicePreviewPeaks?: string;
}

// Single source of truth for "which S3 key does TTS actually use?"
export function effectiveReferenceKey(
  ref: Pick<Reference, "s3Key" | "defaultClipS3Key">
): string {
  return ref.defaultClipS3Key ?? ref.s3Key;
}

export function effectiveDuration(
  ref: Pick<Reference, "duration" | "defaultClipDuration">
): number {
  return ref.defaultClipDuration ?? ref.duration;
}

// ── Video Job ───────────────────────────────────────────────────────────

export type VideoJobStatus = "queued" | "processing" | "complete" | "failed";

// ── H3 (MiniMax-H3 r2v) video config ───────────────────────────────────
// Mirrors H3 Studio's input surface. The previous LTX-2.3 fields
// (qualityMode/resolution/cameraMotion/fps/cfgScale/negativePrompt) are kept
// optional so historical jobs still deserialize; the H3 worker ignores them.
export type H3Aspect = "16:9" | "9:16" | "1:1" | "4:3" | "3:4" | "21:9";
export type H3Quality = "fast" | "quality" | "2k";
export type H3Gpu = "B200" | "H200" | "H100" | "A100-80GB" | "L40S";

export const H3_ASPECTS: H3Aspect[] = ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"];
export const H3_QUALITIES: H3Quality[] = ["fast", "quality", "2k"];
export const H3_GPUS: H3Gpu[] = ["H200", "H100", "B200", "A100-80GB", "L40S"];

// H3 generates 5-15s at 24fps. A model limit, not a knob — a longer TTS take
// is animated over a user-chosen window (audioStartS).
export const H3_MAX_SECONDS = 15;
export const H3_MIN_SECONDS = 2;

export interface VideoJob {
  videoJobId: string;
  parentJobId: string;
  userId: string;
  status: VideoJobStatus;
  // Config
  prompt: string;
  aspect: H3Aspect;
  quality: H3Quality;
  gpu: H3Gpu;
  steps?: number;
  seed?: number;
  // References. sourceImageKey stays as the first reference image so existing
  // rows keep rendering; refImageKeys is the full list.
  sourceImageKey: string;
  refImageKeys?: string[];
  refVideoKeys?: string[];
  // Presigned GETs for the two above, added by the read endpoints so Reload
  // can repopulate the reference tiles. Not stored.
  refImageUrls?: string[];
  refVideoUrls?: string[];
  // What each reference IS, in the user's words — this is what lets the prompt
  // give each one its own <Subject N> instead of assuming they are all the
  // same person, which is why extra references used to look ignored.
  refImageLabels?: string[];
  refVideoLabels?: string[];
  /** The setting and action, in the user's words. Replaces the default framing. */
  scene?: string;
  // Audio comes from the parent TTS job automatically — from the take the
  // user picked, defaulting to the first completed one.
  audioKey: string;
  takeIndex?: number;
  audioStartS?: number;
  durationS: number;
  // Output
  outputKey?: string;
  // Presigned playback URL — not in the list endpoint, fetched per card.
  videoUrl?: string;
  // Error
  error?: string;
  // Timestamps
  createdAt: string;
  completedAt?: string;
  // Legacy DDB-side TTL — see Job.ttl note.
  ttl?: number;
  // ── Legacy LTX fields, retained for old rows only ──
  qualityMode?: "fast" | "quality" | "hq";
  resolution?: "720p" | "1080p";
  cameraMotion?: string;
  fps?: number;
  negativePrompt?: string;
  cfgScale?: number;
  numInferenceSteps?: number;
}

export interface VideoJobCreate {
  prompt: string;
  aspect: H3Aspect;
  quality: H3Quality;
  gpu?: H3Gpu;
  steps?: number;
  seed?: number;
  refImageKeys: string[];
  refVideoKeys?: string[];
  audioStartS?: number;
  /** Which of the parent job's takes to use. Defaults to the first complete. */
  takeIndex?: number;
  // Client-side only: presigned previews carried by Reload so the panel can
  // show the references it is restoring. Ignored by the API.
  refImageUrls?: string[];
  refVideoUrls?: string[];
  // What each reference IS, in the user's words, so the prompt can give each
  // one its own <Subject N> instead of assuming they are all the same person.
  refImageLabels?: string[];
  refVideoLabels?: string[];
  /** The setting and action, in the user's words. */
  scene?: string;
}

// ── API Responses ──────────────────────────────────────────────────────
export interface PaginatedResult<T> {
  items: T[];
  cursor?: string;
}

export interface Stats {
  totalUsers: number;
  totalJobs: number;
  completedJobs: number;
  failedJobs: number;
  activeJobs: number;
  ttsJobs: number;
  vcJobs: number;
  totalGenSeconds: number;
  totalInferenceSeconds: number;
  gpuCost: {
    total: number;
    today: number;
    week: number;
    month: number;
    avgPerJob: number;
  };
}

// ── Submit Job ─────────────────────────────────────────────────────────
export interface SubmitJobRequest {
  type: "tts" | "voice-convert";
  text?: string;
  referenceName: string;
  referenceKey: string;
  model?: TtsModel;
  referenceTranscript?: string;
  exaggeration?: number;
  cfgWeight?: number;
  temperature?: number;
  repetitionPenalty?: number;
  topP?: number;
  minP?: number;
  topK?: number;
  cloneMode?: MossCloneMode;
  trimStart?: number;
  trimEnd?: number;
  language?: string;
  sourceKey?: string;
  diffusionSteps?: number;
  similarityCfgRate?: number;
  intelligibilityCfgRate?: number;
  lengthAdjust?: number;
}
